import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage, RadioGroup } from "@patient-watch/ui";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import { useSelectEntry } from "../../hooks/useSelectEntry";
import StyledRadioItem from "../../components/StyledRadioItem";
import { useCopy } from "../../i18n/locale";

const formSchema = (message: string) =>
    z.object({
        glucose_issue: z.enum(["high", "low", "both", "none"], {
            required_error: message,
        }),
    });

type FormData = z.infer<ReturnType<typeof formSchema>>;

export const Route = createFileRoute("/form/glucose-issue")({
    component: GlucoseIssueComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function GlucoseIssueComponent() {
    const { entry_id, profile_id } = useSearch({ from: "/form/glucose-issue" });
    const navigate = useNavigate();
    const copy = useCopy();

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema(copy.common.selectOption)),
    });

    useFormData({
        entry_id,
        form,
        fields: ["glucose_issue"],
    });

    const { data: entryData } = useSelectEntry(entry_id);

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        nextRoute: "/form/notes",
    });

    const onSubmit = async (data: FormData) => {
        await submitForm({
            glucose_issue: data.glucose_issue,
        });
    };

    const getPreviousRoute = () => {
        if (!entryData || entryData.at_sea === false) {
            return "/form/at-sea";
        }

        return "/form/on-duty-intervals";
    };

    const onPrevious = () => {
        navigate({
            to: getPreviousRoute(),
            search: { entry_id, profile_id } as any,
        });
    };

    const options = [
        {
            value: "high",
            label: copy.glucose.high,
            description: copy.glucose.highDescription,
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            selectedBgColor: "bg-red-100",
            selectedBorderColor: "border-red-400",
        },
        {
            value: "low",
            label: copy.glucose.low,
            description: copy.glucose.lowDescription,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            selectedBgColor: "bg-blue-100",
            selectedBorderColor: "border-blue-400",
        },
        {
            value: "both",
            label: copy.glucose.both,
            description: copy.glucose.bothDescription,
            color: "text-yellow-600",
            bgColor: "bg-yellow-50",
            borderColor: "border-yellow-200",
            selectedBgColor: "bg-yellow-100",
            selectedBorderColor: "border-yellow-400",
        },
        {
            value: "none",
            label: copy.glucose.none,
            description: copy.glucose.noneDescription,
            color: "text-green-600",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
            selectedBgColor: "bg-green-100",
            selectedBorderColor: "border-green-400",
        },
    ];

    return (
        <FormStepWrapper currentStep={4}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 py-4 w-full max-w-md">
                    <h2 className="block font-semibold text-xl text-center">{copy.glucose.question}</h2>
                    <p className="text-gray-600 text-sm text-center">{copy.glucose.hint}</p>

                    <FormField
                        control={form.control}
                        name="glucose_issue"
                        render={({ field }) => (
                            <FormItem className="space-y-3">
                                <FormControl>
                                    <RadioGroup
                                        onValueChange={field.onChange}
                                        value={field.value}
                                        className="space-y-3"
                                    >
                                        {options.map((option) => {
                                            const isSelected = field.value === option.value;

                                            return (
                                                <StyledRadioItem
                                                    key={option.value}
                                                    option={option}
                                                    isSelected={isSelected}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormButtons submitting={submitting} showPrevious onPrevious={onPrevious} />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
